import useAuthStore from '@/stores/AuthStore'
import useSnippetStore from '@/stores/SnippetStore'
import { useOrderStore } from '@/stores/OrderStore'
import useUrlStore from '@/stores/UrlStore'
import useConversationStore from '@/stores/ConversationStore'
import { useThemeContext } from '@/stores/ThemeContext'
import { DownOutlined } from '@ant-design/icons'
import {
  Avatar,
  Col,
  Dropdown,
  Layout,
  Menu,
  Row,
  Space,
  Typography,
  theme,
  Badge,
} from 'antd'
import 'dayjs/locale/zh-cn'
import { useEffect, useMemo, useRef } from 'react'
import { Link, NavLink, Outlet, useHref } from 'react-router-dom'

import '@/assets/App.css'
import AppLogo from '@/assets/highlights_travel_300_300_250613.png'
import 'react-chat-elements/dist/main.css'
import ReloadPrompt from './ReloadPrompt'
import ClearCache from './ClearCache'

import { BUILD_VERSION, GIT_HEAD } from '@/config'

const { Header, Footer, Content } = Layout
const { Title } = Typography

const HEADER_STYLE = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
  width: '100%',
  background: 'white',
}

const BADGE_STYLE = { backgroundColor: '#52c41a' }

const USER_COL_STYLE = {
  color: 'white',
  marginBottom: '0',
  display: 'flex',
  justifyContent: 'end',
}

const BASE_MENU_ITEMS = [
  { key: '/order/follow', label: <Link to='/order/follow'>订单跟踪</Link> },
  { key: '/order/chat', label: <Link to='/order/chat'>在线聊天</Link> },
  { key: '/chat/history', label: <Link to='/chat/history'>历史记录</Link> },
  { key: '/customer_relation/index', label: <Link to='/customer_relation/index'>客户运营</Link> },
  { key: '/customer/profile', label: <Link to='/customer/profile'>客户画像</Link> },
]

function getDefaultPath(href) {
  if (href === '/') {
    return '/order/follow'
  }
  const splitPath = href.split('/')
  if (splitPath.length > 2) {
    return '/' + splitPath[1] + '/' + splitPath[2]
  }
  return '/order/follow'
}

function DesktopApp() {
  const { colorPrimary } = useThemeContext()

  const href = useHref()

  const loginUser = useAuthStore(state => state.loginUser)
  const totalNotify = useConversationStore(state => state.totalNotify)
  const openSnippetDrawer = useSnippetStore(state => state.openDrawer)
  const openPaymentDrawer = useOrderStore(state => state.openDrawer)
  const openShorturlDrawer = useUrlStore(state => state.openDrawer)

  const onClick = ({ key }) => {
    if (key === 'snippet-list') {
      openSnippetDrawer()
    } else if (key === 'generate-payment') {
      openPaymentDrawer()
    } else if (key === 'shorturl-conversion') {
      openShorturlDrawer()
    }
  }

  const defaultPath = getDefaultPath(href)

  const {
    token: { colorBgContainer },
  } = theme.useToken()

  const contentStyle = useMemo(
    () => ({ padding: 8, margin: 0, minHeight: 280, background: colorBgContainer }),
    [colorBgContainer],
  )

  /**
   * 标签页标题闪烁
   * 使用 ref 保存闪烁状态和通知数，避免 interval 频繁重建
   */
  const isTitleVisibleRef = useRef(true)
  const totalNotifyRef = useRef(totalNotify)
  totalNotifyRef.current = totalNotify

  useEffect(() => {
    let intervalId

    if (totalNotify > 0) {
      if ('setAppBadge' in navigator) {
        navigator.setAppBadge(totalNotify).catch(() => {})
      }
      intervalId = setInterval(() => {
        isTitleVisibleRef.current = !isTitleVisibleRef.current
        document.title = isTitleVisibleRef.current
          ? `🔔🔥💬【${totalNotifyRef.current}条新消息】`
          : '______________'
      }, 1000)
    } else {
      document.title = '销售平台'
      if ('clearAppBadge' in navigator) {
        navigator.clearAppBadge().catch(() => {})
      }
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [totalNotify])

  const menuItems = useMemo(() => {
    const items = BASE_MENU_ITEMS.map(item => {
      if (item.key === '/order/chat') {
        return {
          ...item,
          label: (
            <Link to='/order/chat'>
              在线聊天
              <Badge
                count={totalNotify > 0 ? totalNotify : undefined}
                style={BADGE_STYLE}
              />
            </Link>
          ),
        }
      }
      return item
    })

    return items
  }, [totalNotify])

  const dropdownMenu = useMemo(
    () => ({
      items: [
        { label: <Link to='/account/profile'>个人资料</Link>, key: 'profile' },
        { type: 'divider' },
        { label: '支付链接', key: 'generate-payment' },
        { label: '短链接转换', key: 'shorturl-conversion' },
        { label: '图文集', key: 'snippet-list' },
        { type: 'divider' },
        { label: <ReloadPrompt force />, key: 'reload' },
        { type: 'divider' },
        { label: <ClearCache />, key: 'clearcache' },
        { type: 'divider' },
        { label: <Link to='/p/dingding/logout'>退出</Link>, key: 'logout' },
      ],
      onClick,
    }),
    [],
  )

  const userLinkStyle = useMemo(() => ({ color: colorPrimary }), [colorPrimary])

  return (
    <Layout>
      <Header className="header" style={HEADER_STYLE}>
        <Row gutter={{ md: 24 }} align="middle">
          <Col flex="240px">
            <NavLink to="/">
              <img src={AppLogo} className="logo" alt="App logo" />
            </NavLink>
            <Title level={3}>销售平台</Title>
          </Col>
          <Col span={10}>
            <Menu
              mode="horizontal"
              selectedKeys={[defaultPath]}
              items={menuItems}
            />
          </Col>
          <Col flex="auto" style={USER_COL_STYLE}>
            <ReloadPrompt style={{ marginRight: 'auto' }} />
            <Dropdown menu={dropdownMenu} trigger={['click']}>
              <a onClick={e => e.preventDefault()} style={userLinkStyle}>
                <Space>
                  <Avatar src={loginUser.avatarUrl}>
                    {loginUser?.username?.substring(1)}
                  </Avatar>
                  {loginUser.username}
                  <DownOutlined />
                </Space>
              </a>
            </Dropdown>
          </Col>
        </Row>
      </Header>
      <Layout>
        <Content style={contentStyle}>
          <Outlet />
        </Content>
      </Layout>
      <Footer>
        桂林海纳国际旅行社有限公司 Version: {BUILD_VERSION}({GIT_HEAD})
      </Footer>
    </Layout>
  )
}

export default DesktopApp